import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

export interface GroupFeature {
  name: string;
  description: string;
  label: string;
}

export interface FeatureSummary {
  plan_name: string;
  available_features: GroupFeature[];
  selected_features: string[];
  total_available: number;
  total_selected: number;
}

export function useGroupFeatures() {
  const queryClient = useQueryClient();

  // Get available features for the current group
  const { data: availableFeatures, isLoading: isLoadingAvailable, error: availableError } = useQuery({
    queryKey: ['group-features', 'available'],
    queryFn: async () => {
      const response = await api.get('/group-features/available');
      return response.data.data as GroupFeature[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get selected features for the current group
  const { data: selectedFeatures, isLoading: isLoadingSelected, error: selectedError } = useQuery({
    queryKey: ['group-features', 'selected'],
    queryFn: async () => {
      const response = await api.get('/group-features/selected');
      return response.data.data as string[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get feature summary
  const { data: featureSummary, isLoading: isLoadingSummary, error: summaryError } = useQuery({
    queryKey: ['group-features', 'summary'],
    queryFn: async () => {
      const response = await api.get('/group-features/summary');
      return response.data.data as FeatureSummary;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update selected features
  const updateFeaturesMutation = useMutation({
    mutationFn: async (features: string[]) => {
      const response = await api.put('/group-features/selected', { features });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Features updated successfully');
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['group-features', 'selected'] });
      queryClient.invalidateQueries({ queryKey: ['group-features', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['features'] }); // Also invalidate general features
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update features');
    }
  });

  // Check if a specific feature is selected
  const isFeatureSelected = (featureName: string): boolean => {
    return selectedFeatures?.includes(featureName) || false;
  };

  // Check if a specific feature is available
  const isFeatureAvailable = (featureName: string): boolean => {
    return availableFeatures?.some(f => f.name === featureName) || false;
  };

  // Check if group has access to a feature (alias for isFeatureAvailable for backward compatibility)
  const hasFeature = (featureName: string): boolean => {
    return isFeatureAvailable(featureName);
  };

  return {
    // Data
    availableFeatures: availableFeatures || [],
    selectedFeatures: selectedFeatures || [],
    featureSummary,
    
    // Loading states
    isLoading: isLoadingAvailable || isLoadingSelected || isLoadingSummary,
    isLoadingAvailable,
    isLoadingSelected,
    isLoadingSummary,
    
    // Error states
    error: availableError || selectedError || summaryError,
    availableError,
    selectedError,
    summaryError,
    
    // Actions
    updateFeatures: updateFeaturesMutation.mutate,
    isUpdating: updateFeaturesMutation.isPending,
    
    // Helper functions
    isFeatureSelected,
    isFeatureAvailable,
    hasFeature, // Alias for isFeatureAvailable
    
    // Refetch functions
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ['group-features'] });
    }
  };
} 